import React, { useState } from 'react';
import Avatar from '@material-ui/core/Avatar';
import Button from '@material-ui/core/Button';
import CssBaseline from '@material-ui/core/CssBaseline';
import Link from '@material-ui/core/Link';
import { useHistory, useLocation } from 'react-router-dom';
import Grid from '@material-ui/core/Grid';
import { MdArrowBack, MdVisibility, MdVisibilityOff } from 'react-icons/md';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import Container from '@material-ui/core/Container';
import { useDialog } from 'muibox';
import { defaultAlertOptions } from 'config/muibox';

import { useForm, Controller } from "react-hook-form";
import { yupResolver } from '@hookform/resolvers';
import * as yup from 'yup';

import AuthService from 'services/axios/AuthService';
import Logo from 'assets/images/logo512.png';
import { FormControl, FormHelperText, IconButton, InputAdornment, InputLabel, OutlinedInput, Paper } from '@material-ui/core';
import queryString from 'query-string';

const messages = { required: "Completá este dato para continuar" }

const validationSchema = yup.object().shape({
  password: yup
    .string()
    .required(messages.required),
  repetirPassword: yup.string()
    .oneOf([yup.ref('password')], 'Las contraseñas deben coincidir')
});

interface NuevoPasswordPageFields {
  password: string;
  repetirPassword: string;
  token: string;
  email: string;
}

export default function NuevoPasswordPage(props: any) {
  const classes = useStyles();
  const dialog = useDialog();
  const history = useHistory();
  const location = useLocation();

  const [showPassword, setShowPassword] = useState(false);

  const defaultValues: NuevoPasswordPageFields = {
    password: "",
    repetirPassword: "",
    token: "",
    email: ""
  };

  const { errors, handleSubmit, control, reset, register } = useForm<NuevoPasswordPageFields>({
    defaultValues: defaultValues,
    resolver: yupResolver(validationSchema)
  });

  const inputLabelPassword = React.useRef<HTMLLabelElement>(null);
  const inputLabelRepetirPassword = React.useRef<HTMLLabelElement>(null);
  const [labelWidthPassword, setLabelWidthPassword] = React.useState(0);
  const [labelWidthRepetirPassword, setLabelWidthRepetirPassword] = React.useState(0);

  React.useEffect(() => {
    const queryParams = queryString.parse(location.search);
    setLabelWidthPassword(inputLabelPassword.current!.offsetWidth);
    setLabelWidthRepetirPassword(inputLabelRepetirPassword.current!.offsetWidth);
    reset({ ...defaultValues, email: `${queryParams.email}`, token: `${queryParams.token}` })
  }, []);// eslint-disable-line react-hooks/exhaustive-deps

  const onSubmit = async (data: any) => {
    try {
      await AuthService.resetPassword(data.email, data.password, data.repetirPassword, data.token);
      await dialog.alert({ ...defaultAlertOptions, message: 'Tu contraseña fue reestablecida. Ya podes ingresar.' });
      history.push('/login');
    } catch (error) {
      error && //if not cancelled 
        dialog.alert({
          ...defaultAlertOptions,
          title: `${error.message}`,
          message: `${error.errors}`
        });
    }
    return;
  }

  return (
    <Grid container component="main" className={classes.root}>
      <CssBaseline />
      {/*
      //esto es para que se muestre una imagen al costado en desktop
      <Grid item xs={false} sm={4} md={7} className={classes.image} /> 
      */}
      <Grid item xs={12} sm={12} md={12} component={Paper} elevation={6} square>
        <div className={classes.drawerHeader}>
          <IconButton onClick={() => { history.push("/login") }} color="inherit" >
            <MdArrowBack />
          </IconButton>
        </div>
        <div className={classes.drawerSubHeader}>
          <div className={classes.drawerSubHeaderTitle}>{"RESTABLECER CONTASEÑA"}</div>
          <div className={classes.drawerSubHeaderSubTitle}>{"Ingresa una nueva contraseña"}</div>
          <div className={classes.drawerSubHeaderAvatarContainer}>
            <Avatar src={Logo} className={classes.drawerSubHeaderAvatar} alt="logo" />
          </div>
        </div>
        <div className={classes.paper}>
          <form onSubmit={handleSubmit(onSubmit)} noValidate autoComplete="off"
            className={classes.form}
          >
            <input type="hidden" ref={register} name="email" />
            <input type="hidden" ref={register} name="token" />
            <Container maxWidth="sm" style={{ paddingTop: 24, paddingLeft: 24, paddingRight: 24 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Controller
                    as={
                      <FormControl variant="outlined"
                        fullWidth required
                        error={!!errors.password}
                      >
                        <InputLabel ref={inputLabelPassword} htmlFor="outlined-adornment-password">
                          {"Contraseña"}
                        </InputLabel>
                        <OutlinedInput
                          id="outlined-adornment-password"
                          type={showPassword ? 'text' : 'password'}
                          endAdornment={
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="toggle password visibility"
                                onClick={() => { setShowPassword(!showPassword) }}
                                edge="end"
                              >
                                {showPassword ? <MdVisibility /> : <MdVisibilityOff />}
                              </IconButton>
                            </InputAdornment>
                          }
                          labelWidth={labelWidthPassword}
                        />
                        {errors.password && errors.password.message &&
                          < FormHelperText > {errors.password.message}</FormHelperText>
                        }
                      </FormControl>
                    }
                    name="password"
                    control={control}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    as={
                      <FormControl variant="outlined"
                        fullWidth required
                        error={!!errors.repetirPassword}
                      >
                        <InputLabel ref={inputLabelRepetirPassword} htmlFor="outlined-adornment-password">
                          {"Repetir Contraseña"}
                        </InputLabel>
                        <OutlinedInput
                          id="outlined-adornment-password"
                          type={showPassword ? 'text' : 'password'}
                          endAdornment={
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="toggle password visibility"
                                onClick={() => { setShowPassword(!showPassword) }}
                                edge="end"
                              >
                                {showPassword ? <MdVisibility /> : <MdVisibilityOff />}
                              </IconButton>
                            </InputAdornment>
                          }
                          labelWidth={labelWidthRepetirPassword}
                        />
                        {errors.repetirPassword && errors.repetirPassword.message &&
                          < FormHelperText > {errors.repetirPassword.message}</FormHelperText>
                        }
                      </FormControl>
                    }
                    name="repetirPassword"
                    control={control}
                  />
                </Grid>
                <Grid item xs={12} style={{ paddingTop: 24 }}>
                  <Button
                    type="submit"
                    // disabled={!user || !password}
                    fullWidth
                    variant="contained"
                    color="secondary"
                    size="large"
                    style={{ padding: 12 }}
                  >
                    {"RESTABLECER CONTASEÑA"}
                  </Button>
                </Grid>
                <Grid item xs={12} style={{ display: 'flex', justifyContent: 'center', paddingTop: 8 }}>
                  <Link href="/login" variant="subtitle2" style={{ textDecoration: 'none' }} >
                    {"volver al inicio".toUpperCase()}
                  </Link>
                </Grid>
              </Grid>
            </Container>
          </form>
        </div>
      </Grid>
    </Grid>
  );
}

const useStyles = makeStyles((theme: Theme) => {
  const headerColor = {
    color: '#fff',
    subColor: '#ccc',
    backgroundColor: theme.palette.primary.main,
    avatarBackgroundColor: '#fff'
  }
  return createStyles({
    '@global': {
      body: {
        backgroundColor: theme.palette.common.white,
      },
    },
    root: {
      height: '100vh',
    },
    paper: {
      marginTop: theme.spacing(6),
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
    avatar: {
      margin: theme.spacing(1),
      backgroundColor: theme.palette.secondary.main,
    },
    form: {
      width: '100%', // Fix IE 11 issue.
      marginTop: theme.spacing(1),
    },
    drawerHeader: {
      ...theme.mixins.toolbar,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      padding: theme.spacing(0, 1),
      color: headerColor.color,
      backgroundColor: headerColor.backgroundColor,
    },
    drawerSubHeader: {
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'start',
      alignItems: 'center',
      textAlign: 'center',
      height: 120,
      backgroundColor: headerColor.backgroundColor
    },
    drawerSubHeaderAvatarContainer: {
      position: 'absolute',
      backgroundColor: headerColor.backgroundColor,
      top: 60,
      padding: 12,
      borderRadius: '50%',
      zIndex: 1,
    },
    drawerSubHeaderAvatar: {
      width: theme.spacing(10),
      height: theme.spacing(10),
      backgroundColor: headerColor.avatarBackgroundColor,
      padding: 8
    },
    drawerSubHeaderTitle: {
      color: headerColor.color,
      fontSize: '1.25rem'
    },
    drawerSubHeaderSubTitle: {
      color: headerColor.subColor,
      fontSize: '0.8rem',
      paddingTop: 5,
    },
  });
});